import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import { CompanySelectionGateUnitOfAnalysisSync } from './CompanySelectionGateUnitOfAnalysisSync';

export type CompanySelectionGateStatus = 'idle' | 'pending' | 'error';

type CompanySelectionGateContextValue = {
  status: CompanySelectionGateStatus;
  startRequest: (instanceId: string) => {
    complete: () => void;
    fail: () => void;
  };
  resetRequest: (instanceId: string) => void;
};

const CompanySelectionGateContext =
  createContext<CompanySelectionGateContextValue | null>(null);

export const CompanySelectionGateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [statuses, setStatuses] = useState<
    Record<string, { status: 'pending' | 'error'; token: object }>
  >({});

  const statusValues = Object.values(statuses).map((entry) => entry.status);
  const status: CompanySelectionGateStatus = (() => {
    if (statusValues.includes('pending')) {
      return 'pending';
    }
    if (statusValues.includes('error')) {
      return 'error';
    }
    return 'idle';
  })();

  const startRequest = useCallback(
    (
      instanceId: string
    ): {
      complete: () => void;
      fail: () => void;
    } => {
      const token = {};
      setStatuses((prev) => ({
        ...prev,
        [instanceId]: { status: 'pending', token },
      }));

      return {
        complete: () => {
          setStatuses((prev) => {
            if (prev[instanceId]?.token !== token) {
              return prev;
            }
            const next = { ...prev };
            delete next[instanceId];
            return next;
          });
        },
        fail: () => {
          setStatuses((prev) => {
            if (prev[instanceId]?.token !== token) {
              return prev;
            }
            return {
              ...prev,
              [instanceId]: { status: 'error', token },
            };
          });
        },
      };
    },
    []
  );

  const resetRequest = useCallback((instanceId: string) => {
    setStatuses((prev) => {
      if (!(instanceId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[instanceId];
      return next;
    });
  }, []);

  return (
    <CompanySelectionGateContext.Provider
      value={{ status, startRequest, resetRequest }}
    >
      <CompanySelectionGateUnitOfAnalysisSync resetRequest={resetRequest} />
      {children}
    </CompanySelectionGateContext.Provider>
  );
};

export const useCompanySelectionGate = (): CompanySelectionGateContextValue => {
  const context = useContext(CompanySelectionGateContext);
  if (context === null) {
    throw new Error(
      'useCompanySelectionGate must be used within CompanySelectionGateProvider'
    );
  }
  return context;
};
